import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Button, IconButton, Paper, Tooltip, useMediaQuery } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventRoundedIcon from "@material-ui/icons/EventRounded";
import EventAvailableRoundedIcon from "@material-ui/icons/EventAvailableRounded";
import PersonRoundedIcon from "@material-ui/icons/PersonRounded";
import BlockRoundedIcon from "@material-ui/icons/BlockRounded";
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { poderDeAdmin } from "../../utils/perfil";
import { painelAstro } from "../../utils/estiloAstro";
import NovoHorarioModal from "./NovoHorarioModal";
import DetalheHorarioModal from "./DetalheHorarioModal";
import { rotuloStatus } from "./cores";

const ALTURA_HORA = 64;
const HORA_INICIO_PADRAO = 7;
const HORA_FIM_PADRAO = 21;
const PASSO_MINUTOS = 15;
const LARGURA_HORAS = 64;

// Paleta dos horarios: fundo claro, barra forte na esquerda, texto escuro
// (no tema escuro o fundo vira translucido e o texto clareia).
const paleta = (escuro) => ({
  available: {
    fundo: escuro ? "rgba(20,184,166,.14)" : "#ecfdf3",
    fundoHover: escuro ? "rgba(20,184,166,.24)" : "#d9f8e6",
    barra: "#14b8a6",
    texto: escuro ? "#5eead4" : "#115e59",
    suave: escuro ? "#2dd4bf" : "#0f766e",
  },
  booked: {
    fundo: escuro ? "rgba(59,130,246,.22)" : "#eaf2ff",
    fundoHover: escuro ? "rgba(59,130,246,.34)" : "#dbe8ff",
    barra: "#3b82f6",
    texto: escuro ? "#bfdbfe" : "#1e3a8a",
    suave: escuro ? "#93c5fd" : "#2563eb",
  },
  blocked: {
    fundo: escuro
      ? "repeating-linear-gradient(135deg, rgba(148,163,184,.16) 0 6px, rgba(148,163,184,.08) 6px 12px)"
      : "repeating-linear-gradient(135deg, #eef1f5 0 6px, #f7f8fa 6px 12px)",
    fundoHover: escuro ? "rgba(148,163,184,.22)" : "#e8ecf1",
    barra: "#94a3b8",
    texto: escuro ? "#cbd5e1" : "#475569",
    suave: "#94a3b8",
  },
});

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const linha = escuro ? "rgba(148,163,184,.13)" : "#e8edf2";
  const linhaFraca = escuro ? "rgba(148,163,184,.07)" : "#f1f4f7";
  const fundoCard = theme.palette.background.paper;
  const textoFraco = theme.palette.text.secondary;
  const verde = "#0D9488";
  return {
    // Ocupa a altura toda da area de conteudo e rola por dentro.
    root: {
      height: "100%",
      boxSizing: "border-box",
      padding: theme.spacing(2),
      overflowY: "auto",
      backgroundColor: "transparent",
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("xs")]: { padding: theme.spacing(1) },
    },

    // ── Cabecalho da pagina ──
    topo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
      marginBottom: theme.spacing(2),
    },
    titulo: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      fontSize: "1.45rem",
      fontWeight: 800,
      color: theme.palette.text.primary,
      lineHeight: 1.15,
    },
    tituloIcone: {
      width: 44,
      height: 44,
      borderRadius: 14,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      background: "linear-gradient(135deg,#2dd4bf,#0D9488)",
      boxShadow: "0 8px 20px rgba(13,148,136,.28)",
      flexShrink: 0,
    },
    subtitulo: { fontSize: 13, color: textoFraco, fontWeight: 500, marginTop: 3 },
    acoes: { display: "flex", gap: 8, flexWrap: "wrap" },
    botaoPrimario: {
      borderRadius: 12,
      padding: "9px 18px",
      textTransform: "none",
      fontWeight: 800,
      color: "#fff",
      background: "linear-gradient(135deg,#14b8a6,#0D9488)",
      boxShadow: "0 8px 18px rgba(13,148,136,.28)",
      "&:hover": { background: "linear-gradient(135deg,#0d9488,#0f766e)" },
    },
    botaoSecundario: {
      borderRadius: 12,
      padding: "9px 16px",
      textTransform: "none",
      fontWeight: 700,
      color: escuro ? "#fca5a5" : "#dc2626",
      border: `1px solid ${escuro ? "rgba(248,113,113,.35)" : "#fecaca"}`,
      backgroundColor: escuro ? "rgba(239,68,68,.08)" : "#fff",
      "&:hover": { backgroundColor: escuro ? "rgba(239,68,68,.16)" : "#fef2f2" },
    },

    corpo: {
      display: "grid",
      gridTemplateColumns: "280px minmax(0, 1fr)",
      gap: theme.spacing(2),
      alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },

    // ── Barra lateral ──
    lateral: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      [theme.breakpoints.down("sm")]: { order: 2 },
    },
    cartao: { ...painelAstro(theme), padding: 16 },
    cartaoTitulo: {
      fontWeight: 800,
      fontSize: 13,
      letterSpacing: ".04em",
      textTransform: "uppercase",
      color: textoFraco,
      marginBottom: 10,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    miniTopo: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
    miniMes: { fontWeight: 800, fontSize: 14.5, color: theme.palette.text.primary },
    miniGrade: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: 2 },
    miniNome: { textAlign: "center", fontSize: 10.5, fontWeight: 800, color: textoFraco, padding: "4px 0" },
    miniDia: {
      position: "relative",
      height: 32,
      width: 32,
      margin: "0 auto",
      borderRadius: "50%",
      border: "none",
      background: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: 700,
      color: theme.palette.text.primary,
      "&:hover": { background: escuro ? "rgba(255,255,255,.07)" : "#eef6f1" },
    },
    miniFora: { color: escuro ? "rgba(255,255,255,.28)" : "#b8c2cc" },
    miniHoje: { color: verde, boxShadow: `inset 0 0 0 2px ${verde}` },
    miniSelecionado: {
      background: "linear-gradient(135deg,#14b8a6,#0D9488) !important",
      color: "#fff !important",
      boxShadow: "0 4px 10px rgba(13,148,136,.35)",
    },
    miniNaSemana: {
      background: escuro ? "rgba(20,184,166,.16)" : "#e3f6ea",
      color: escuro ? "#5eead4" : "#115e59",
    },
    miniPontos: {
      position: "absolute",
      bottom: 2,
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      gap: 2,
    },
    miniPonto: { width: 4, height: 4, borderRadius: "50%" },
    legendaLinha: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "7px 2px",
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& b": { marginLeft: "auto", fontWeight: 800 },
    },
    legendaCor: { width: 12, height: 12, borderRadius: 4, flexShrink: 0 },
    proxima: {
      display: "flex",
      gap: 10,
      alignItems: "center",
      padding: "9px 8px",
      margin: "0 -8px",
      borderRadius: 12,
      cursor: "pointer",
      "&:hover": { backgroundColor: escuro ? "rgba(255,255,255,.04)" : "#f3f8f5" },
    },
    avatar: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      fontSize: 13,
      color: "#fff",
      background: "linear-gradient(135deg,#60a5fa,#2563eb)",
    },
    proximaNome: {
      fontWeight: 700,
      fontSize: 13.5,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    proximaSub: { fontSize: 12, color: textoFraco },
    vazio: { fontSize: 13, color: textoFraco, padding: "4px 2px", lineHeight: 1.5 },

    // ── Calendario ──
    calendario: {
      ...painelAstro(theme),
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    barra: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "14px 16px",
      flexWrap: "wrap",
      borderBottom: `1px solid ${linha}`,
    },
    botaoHoje: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 800,
      padding: "5px 14px",
      border: `1px solid ${linha}`,
      marginRight: 4,
    },
    tituloPeriodo: {
      fontWeight: 800,
      fontSize: "1.15rem",
      marginLeft: 8,
      flex: 1,
      minWidth: 180,
      color: theme.palette.text.primary,
    },
    grupoVisao: {
      display: "inline-flex",
      padding: 3,
      gap: 2,
      borderRadius: 12,
      background: escuro ? "rgba(255,255,255,.05)" : "#f1f5f3",
    },
    botaoVisao: {
      border: "none",
      background: "transparent",
      padding: "6px 14px",
      borderRadius: 9,
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      color: textoFraco,
      fontFamily: "inherit",
      "&:hover": { color: theme.palette.text.primary },
    },
    botaoVisaoAtivo: {
      background: fundoCard,
      color: `${verde} !important`,
      boxShadow: escuro ? "0 0 0 1px rgba(45,212,191,.35)" : "0 1px 4px rgba(15,23,42,.12)",
    },

    rolagemX: { overflowX: "auto" },
    semana: { minWidth: 820 },
    cabecalhoDias: {
      display: "grid",
      borderBottom: `1px solid ${linha}`,
      backgroundColor: fundoCard,
    },
    cantoFuso: {
      fontSize: 10,
      fontWeight: 700,
      color: textoFraco,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      paddingBottom: 8,
    },
    cabecalhoDia: {
      padding: "10px 4px 10px",
      textAlign: "center",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      borderLeft: `1px solid ${linhaFraca}`,
      "&:hover $numeroDia": { background: escuro ? "rgba(255,255,255,.07)" : "#eef6f1" },
    },
    nomeDia: {
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: ".08em",
      color: textoFraco,
    },
    nomeDiaHoje: { color: verde },
    numeroDia: {
      fontSize: 22,
      fontWeight: 800,
      lineHeight: 1,
      width: 42,
      height: 42,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.primary,
      transition: "background .15s ease",
    },
    numeroHoje: {
      background: "linear-gradient(135deg,#14b8a6,#0D9488) !important",
      color: "#fff",
      boxShadow: "0 6px 14px rgba(13,148,136,.35)",
    },
    pilulas: { display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center", minHeight: 20 },
    pilula: {
      fontSize: 10.5,
      fontWeight: 800,
      borderRadius: 999,
      padding: "2px 8px",
      whiteSpace: "nowrap",
    },

    corpoGrade: {
      position: "relative",
      maxHeight: "calc(100vh - 290px)",
      minHeight: 360,
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    grade: { display: "grid", position: "relative" },
    colunaHoras: { position: "relative" },
    rotuloHora: {
      position: "absolute",
      right: 10,
      fontSize: 11,
      fontWeight: 600,
      color: textoFraco,
      transform: "translateY(-50%)",
      background: fundoCard,
      padding: "0 2px",
    },
    colunaDia: {
      position: "relative",
      borderLeft: `1px solid ${linha}`,
      userSelect: "none",
      backgroundImage: `repeating-linear-gradient(to bottom, ${linha} 0, ${linha} 1px, transparent 1px, transparent ${
        ALTURA_HORA / 2
      }px, ${linhaFraca} ${ALTURA_HORA / 2}px, ${linhaFraca} ${ALTURA_HORA / 2 + 1}px, transparent ${
        ALTURA_HORA / 2 + 1
      }px, transparent ${ALTURA_HORA}px)`,
    },
    colunaFimDeSemana: { backgroundColor: escuro ? "rgba(255,255,255,.015)" : "#fafbfc" },
    colunaHoje: { backgroundColor: escuro ? "rgba(20,184,166,.035)" : "rgba(20,184,166,.035)" },
    passado: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      pointerEvents: "none",
      background: escuro ? "rgba(0,0,0,.18)" : "rgba(241,245,249,.65)",
    },
    podeCriar: { cursor: "crosshair" },

    evento: {
      position: "absolute",
      borderRadius: 10,
      padding: "4px 8px 4px 10px",
      overflow: "hidden",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 1,
      transition: "transform .12s ease, box-shadow .12s ease, background .12s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        background: "var(--barra)",
      },
      "&:hover": {
        transform: "translateY(-1px)",
        background: "var(--fundoHover) !important",
        boxShadow: escuro ? "0 8px 20px rgba(0,0,0,.45)" : "0 8px 18px rgba(15,23,42,.12)",
        zIndex: 3,
      },
    },
    eventoPassado: { opacity: 0.55 },
    eventoLinha: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
      overflow: "hidden",
      fontSize: 12,
      lineHeight: 1.25,
    },
    eventoHora: { fontWeight: 800, flexShrink: 0 },
    eventoTexto: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", opacity: 0.9 },
    eventoAvatar: {
      width: 18,
      height: 18,
      borderRadius: "50%",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 10,
      fontWeight: 800,
      color: "#fff",
      background: "#3b82f6",
    },
    selecao: {
      position: "absolute",
      left: 4,
      right: 4,
      borderRadius: 10,
      background: "rgba(20,184,166,.18)",
      border: `2px dashed ${verde}`,
      pointerEvents: "none",
      fontSize: 12,
      fontWeight: 800,
      color: escuro ? "#5eead4" : "#115e59",
      padding: "4px 8px",
      zIndex: 4,
    },
    agora: {
      position: "absolute",
      left: -1,
      right: 0,
      height: 2,
      background: "#ef4444",
      zIndex: 3,
      pointerEvents: "none",
      "&::before": {
        content: '""',
        position: "absolute",
        left: -6,
        top: -5,
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "#ef4444",
        boxShadow: "0 0 0 3px rgba(239,68,68,.2)",
      },
    },
    agoraRotulo: {
      position: "absolute",
      right: 6,
      transform: "translateY(-50%)",
      fontSize: 10.5,
      fontWeight: 800,
      color: "#fff",
      background: "#ef4444",
      borderRadius: 6,
      padding: "1px 5px",
      zIndex: 4,
    },
    dica: {
      position: "absolute",
      left: "50%",
      top: 80,
      transform: "translateX(-50%)",
      zIndex: 2,
      textAlign: "center",
      padding: "18px 22px",
      borderRadius: 16,
      maxWidth: 360,
      background: fundoCard,
      border: `1px dashed ${escuro ? "rgba(45,212,191,.35)" : "#b9e6e0"}`,
      boxShadow: escuro ? "0 12px 30px rgba(0,0,0,.4)" : "0 12px 30px rgba(15,23,42,.08)",
      pointerEvents: "auto",
    },
    dicaIcone: { color: verde, fontSize: 34 },
    dicaTitulo: { fontWeight: 800, fontSize: 15, color: theme.palette.text.primary, margin: "6px 0 4px" },
    dicaTexto: { fontSize: 13, color: textoFraco, marginBottom: 12, lineHeight: 1.5 },

    // ── Mes ──
    mes: { display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))" },
    mesCabecalho: {
      padding: "10px 6px",
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: ".08em",
      textTransform: "uppercase",
      color: textoFraco,
      textAlign: "center",
      borderBottom: `1px solid ${linha}`,
    },
    mesCelula: {
      minHeight: 112,
      padding: 8,
      borderBottom: `1px solid ${linha}`,
      borderLeft: `1px solid ${linhaFraca}`,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      transition: "background .12s ease",
      "&:hover": { backgroundColor: escuro ? "rgba(255,255,255,.03)" : "#f6faf7" },
      [theme.breakpoints.down("xs")]: { minHeight: 70, padding: 4 },
    },
    mesForaDoMes: { opacity: 0.4 },
    mesNumero: {
      fontWeight: 800,
      fontSize: 13,
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.primary,
    },
    mesItem: {
      fontSize: 11.5,
      fontWeight: 700,
      borderRadius: 7,
      padding: "3px 7px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };
});

const minutosDoDia = (d) => d.getHours() * 60 + d.getMinutes();
const arredondar = (minutos) => Math.round(minutos / PASSO_MINUTOS) * PASSO_MINUTOS;
const hhmm = (minutos) =>
  `${String(Math.floor(minutos / 60)).padStart(2, "0")}:${String(minutos % 60).padStart(2, "0")}`;
const maiuscula = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const iniciais = (nome) =>
  String(nome || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");

// Coloca lado a lado os horarios que se sobrepoem no mesmo dia.
const distribuirColunas = (eventos) => {
  const ordenados = [...eventos].sort((a, b) => a.inicio - b.inicio || b.fim - a.fim);
  const resultado = [];
  let grupo = [];
  let fimDoGrupo = 0;
  const fecharGrupo = () => {
    const colunas = [];
    grupo.forEach((ev) => {
      let col = colunas.findIndex((fim) => fim <= ev.inicio);
      if (col === -1) {
        col = colunas.length;
        colunas.push(ev.fim);
      } else {
        colunas[col] = ev.fim;
      }
      ev.coluna = col;
    });
    grupo.forEach((ev) => resultado.push({ ...ev, totalColunas: colunas.length }));
    grupo = [];
  };
  ordenados.forEach((ev) => {
    if (grupo.length && ev.inicio >= fimDoGrupo) fecharGrupo();
    grupo.push(ev);
    fimDoGrupo = Math.max(fimDoGrupo, ev.fim);
  });
  if (grupo.length) fecharGrupo();
  return resultado;
};

const Agenda = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const podeEditar = poderDeAdmin(user?.profile);
  const celular = useMediaQuery("(max-width:600px)");
  const tema = useTheme();

  const [visao, setVisao] = useState(() => (window.innerWidth < 600 ? "dia" : "semana"));
  const [referencia, setReferencia] = useState(() => startOfDay(new Date()));
  const [mesMini, setMesMini] = useState(() => startOfMonth(new Date()));
  const [horarios, setHorarios] = useState([]);
  const [doMesMini, setDoMesMini] = useState([]);
  const [proximas, setProximas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [agora, setAgora] = useState(new Date());

  const [novoAberto, setNovoAberto] = useState(false);
  const [novoInicial, setNovoInicial] = useState(null);
  const [novoModo, setNovoModo] = useState("cadastrar");
  const [detalhe, setDetalhe] = useState(null);

  const [selecao, setSelecao] = useState(null);
  const arrastando = useRef(null);
  const corpoRef = useRef(null);
  const rolouRef = useRef("");

  const P = paleta(tema.palette.type === "dark");

  const dias = useMemo(() => {
    if (visao === "dia") return [referencia];
    if (visao === "semana") {
      const inicio = startOfWeek(referencia, { weekStartsOn: 0 });
      return Array.from({ length: 7 }, (_, i) => addDays(inicio, i));
    }
    const inicio = startOfWeek(startOfMonth(referencia), { weekStartsOn: 0 });
    const fim = endOfWeek(endOfMonth(referencia), { weekStartsOn: 0 });
    const lista = [];
    for (let d = inicio; d <= fim; d = addDays(d, 1)) lista.push(d);
    return lista;
  }, [visao, referencia]);

  const intervalo = useMemo(
    () => ({ inicio: dias[0], fim: addDays(dias[dias.length - 1], 1) }),
    [dias]
  );

  const diasMini = useMemo(() => {
    const inicio = startOfWeek(mesMini, { weekStartsOn: 0 });
    const fim = endOfWeek(endOfMonth(mesMini), { weekStartsOn: 0 });
    const lista = [];
    for (let d = inicio; d <= fim; d = addDays(d, 1)) lista.push(d);
    return lista;
  }, [mesMini]);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/interviews", {
        params: { start: intervalo.inicio.toISOString(), end: intervalo.fim.toISOString() },
      });
      setHorarios(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, [intervalo]);

  const carregarMini = useCallback(async () => {
    try {
      const { data } = await api.get("/interviews", {
        params: {
          start: diasMini[0].toISOString(),
          end: addDays(diasMini[diasMini.length - 1], 1).toISOString(),
        },
      });
      setDoMesMini(Array.isArray(data) ? data : []);
    } catch (err) {
      setDoMesMini([]);
    }
  }, [diasMini]);

  const carregarProximas = useCallback(async () => {
    try {
      const { data } = await api.get("/interviews/upcoming");
      setProximas(Array.isArray(data) ? data : []);
    } catch (err) {
      // lista lateral e opcional
    }
  }, []);

  const recarregarTudo = useCallback(() => {
    carregar();
    carregarMini();
    carregarProximas();
  }, [carregar, carregarMini, carregarProximas]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    carregarMini();
  }, [carregarMini]);

  useEffect(() => {
    carregarProximas();
    api
      .get("/users/")
      .then(({ data }) => setUsuarios(data?.users || []))
      .catch(() => setUsuarios([]));
  }, [carregarProximas]);

  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  // A lista lateral se atualiza sozinha: a cada minuto some quem ja terminou
  // ou ja teve o resultado marcado, e a cada 5 minutos busca de novo.
  useEffect(() => {
    const t = setInterval(carregarProximas, 5 * 60000);
    return () => clearInterval(t);
  }, [carregarProximas]);
  const proximasVisiveis = proximas.filter(
    (p) => !p.outcome && new Date(p.endAt || p.startAt).getTime() > agora.getTime()
  );

  // A pagina principal e o mini calendario andam juntos.
  useEffect(() => {
    if (!isSameMonth(referencia, mesMini)) setMesMini(startOfMonth(referencia));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencia]);

  // Tempo real: a IA marca uma entrevista e o calendario atualiza sozinho.
  useEffect(() => {
    const socket = openSocket();
    socket.on("interviewSlot", recarregarTudo);
    return () => socket.off("interviewSlot", recarregarTudo);
  }, [recarregarTudo]);

  // Abre o detalhe atualizado quando o horario muda por fora.
  useEffect(() => {
    if (!detalhe) return;
    const atualizado = horarios.find((h) => h.id === detalhe.id);
    if (atualizado && atualizado.updatedAt !== detalhe.updatedAt) setDetalhe(atualizado);
  }, [horarios, detalhe]);

  const eventosDoDia = useCallback(
    (dia, lista = horarios) =>
      lista
        .filter((h) => isSameDay(new Date(h.startAt), dia))
        .map((h) => {
          const ini = new Date(h.startAt);
          const fim = new Date(h.endAt);
          return {
            ...h,
            inicio: minutosDoDia(ini),
            fim: isSameDay(ini, fim) ? Math.max(minutosDoDia(fim), minutosDoDia(ini) + 15) : 24 * 60,
          };
        }),
    [horarios]
  );

  // Faixa de horas visivel: 07-21 por padrao, estica se houver horario fora.
  const [horaInicio, horaFim] = useMemo(() => {
    let ini = HORA_INICIO_PADRAO;
    let fim = HORA_FIM_PADRAO;
    if (visao !== "mes") {
      dias.forEach((d) =>
        eventosDoDia(d).forEach((ev) => {
          ini = Math.min(ini, Math.floor(ev.inicio / 60));
          fim = Math.max(fim, Math.ceil(ev.fim / 60));
        })
      );
    }
    return [Math.max(0, ini), Math.min(24, fim)];
  }, [dias, eventosDoDia, visao]);

  // Ao abrir uma semana/dia, rola ate o primeiro horario (ou ate agora).
  useEffect(() => {
    if (visao === "mes" || !corpoRef.current) return;
    const chave = `${visao}-${dias[0].toISOString()}`;
    if (rolouRef.current === chave) return;
    const primeiros = dias.flatMap((d) => eventosDoDia(d)).map((e) => e.inicio);
    const hoje = dias.some((d) => isSameDay(d, new Date()));
    const alvo = primeiros.length
      ? Math.min(...primeiros) - 30
      : hoje
      ? minutosDoDia(new Date()) - 60
      : 8 * 60;
    corpoRef.current.scrollTop = Math.max(0, ((alvo - horaInicio * 60) / 60) * ALTURA_HORA);
    if (horarios.length || !primeiros.length) rolouRef.current = chave;
  }, [visao, dias, eventosDoDia, horaInicio, horarios]);

  const resumo = useMemo(() => {
    const agoraMs = Date.now();
    const futuros = horarios.filter((h) => new Date(h.endAt).getTime() > agoraMs);
    return {
      livres: futuros.filter((h) => h.status === "available").length,
      marcadas: horarios.filter((h) => h.status === "booked").length,
      bloqueados: futuros.filter((h) => h.status === "blocked").length,
    };
  }, [horarios]);

  const navegar = (direcao) => {
    if (visao === "dia") setReferencia((r) => addDays(r, direcao));
    else if (visao === "semana") setReferencia((r) => addWeeks(r, direcao));
    else setReferencia((r) => addMonths(r, direcao));
  };

  const textoPeriodo = () => {
    if (visao === "dia") return format(referencia, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR });
    if (visao === "mes") return format(referencia, "MMMM 'de' yyyy", { locale: ptBR });
    const ini = dias[0];
    const fim = dias[6];
    if (isSameMonth(ini, fim)) {
      return `${format(ini, "d")} – ${format(fim, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
    }
    return `${format(ini, "d 'de' MMM", { locale: ptBR })} – ${format(fim, "d 'de' MMM 'de' yyyy", {
      locale: ptBR,
    })}`;
  };

  // ── Arrastar para criar ─────────────────────────────────────────────
  const minutoDoPonteiro = (e, coluna) => {
    const caixa = coluna.getBoundingClientRect();
    const y = Math.min(Math.max(e.clientY - caixa.top, 0), caixa.height);
    return horaInicio * 60 + arredondar((y / ALTURA_HORA) * 60);
  };

  const aoPressionar = (e, dia) => {
    if (!podeEditar || e.button !== 0) return;
    if (e.target !== e.currentTarget) return; // clicou num horario
    const coluna = e.currentTarget;
    const minuto = Math.floor(minutoDoPonteiro(e, coluna) / 30) * 30;
    arrastando.current = { dia, coluna, inicio: minuto, selecao: { dia, inicio: minuto, fim: minuto + 30 } };
    setSelecao(arrastando.current.selecao);
  };

  useEffect(() => {
    const mover = (e) => {
      const a = arrastando.current;
      if (!a) return;
      const minuto = minutoDoPonteiro(e, a.coluna);
      const ini = Math.min(a.inicio, minuto);
      const fim = Math.max(a.inicio + 30, minuto);
      a.selecao = { dia: a.dia, inicio: ini, fim: Math.max(fim, ini + PASSO_MINUTOS) };
      setSelecao(a.selecao);
    };
    const soltar = () => {
      const a = arrastando.current;
      if (!a) return;
      arrastando.current = null;
      setSelecao(null);
      const s = a.selecao;
      setNovoInicial({
        data: format(startOfDay(s.dia), "yyyy-MM-dd"),
        horaInicio: hhmm(s.inicio),
        horaFim: hhmm(Math.min(s.fim, 24 * 60 - 1)),
      });
      setNovoModo("cadastrar");
      setNovoAberto(true);
    };
    window.addEventListener("mousemove", mover);
    window.addEventListener("mouseup", soltar);
    return () => {
      window.removeEventListener("mousemove", mover);
      window.removeEventListener("mouseup", soltar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horaInicio]);

  const abrirModal = (modo) => {
    setNovoInicial(visao === "dia" ? { data: format(referencia, "yyyy-MM-dd") } : null);
    setNovoModo(modo);
    setNovoAberto(true);
  };

  const irParaDia = (dia) => {
    setReferencia(startOfDay(dia));
    setVisao("dia");
  };

  const textoDoEvento = (ev) => {
    if (ev.status === "booked") return ev.candidateName || "Entrevista marcada";
    if (ev.status === "blocked") return "Bloqueado";
    return ev.title || "Livre";
  };

  // ── Semana / dia ─────────────────────────────────────────────────────
  const renderGrade = () => {
    const colunas = `${LARGURA_HORAS}px repeat(${dias.length}, minmax(0, 1fr))`;
    const altura = (horaFim - horaInicio) * ALTURA_HORA;
    const hoje = startOfDay(new Date());
    const minutoAgora = minutosDoDia(agora);
    const semNada = !dias.some((d) => eventosDoDia(d).length);

    return (
      <div className={classes.rolagemX}>
        <div className={visao === "semana" ? classes.semana : undefined}>
          <div className={classes.cabecalhoDias} style={{ gridTemplateColumns: colunas }}>
            <div className={classes.cantoFuso}>GMT-3</div>
            {dias.map((dia) => {
              const doDia = eventosDoDia(dia);
              const livres = doDia.filter((e) => e.status === "available").length;
              const marcadas = doDia.filter((e) => e.status === "booked").length;
              const ehHoje = isSameDay(dia, hoje);
              return (
                <div key={dia.toISOString()} className={classes.cabecalhoDia} onClick={() => irParaDia(dia)}>
                  <div className={`${classes.nomeDia} ${ehHoje ? classes.nomeDiaHoje : ""}`}>
                    {format(dia, visao === "dia" ? "EEEE" : "EEE", { locale: ptBR }).replace(".", "")}
                  </div>
                  <div className={`${classes.numeroDia} ${ehHoje ? classes.numeroHoje : ""}`}>{format(dia, "d")}</div>
                  <div className={classes.pilulas}>
                    {livres > 0 && (
                      <span
                        className={classes.pilula}
                        style={{ background: P.available.fundoHover, color: P.available.suave }}
                      >
                        {livres} {livres === 1 ? "livre" : "livres"}
                      </span>
                    )}
                    {marcadas > 0 && (
                      <span
                        className={classes.pilula}
                        style={{ background: P.booked.fundoHover, color: P.booked.suave }}
                      >
                        {marcadas} {marcadas === 1 ? "entrevista" : "entrevistas"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={classes.corpoGrade} ref={corpoRef}>
            <div className={classes.grade} style={{ gridTemplateColumns: colunas }}>
              <div className={classes.colunaHoras} style={{ height: altura }}>
                {Array.from({ length: horaFim - horaInicio }, (_, i) =>
                  i === 0 ? null : (
                    <div key={i} className={classes.rotuloHora} style={{ top: i * ALTURA_HORA }}>
                      {`${String(horaInicio + i).padStart(2, "0")}:00`}
                    </div>
                  )
                )}
                {dias.some((d) => isSameDay(d, agora)) &&
                  minutoAgora >= horaInicio * 60 &&
                  minutoAgora <= horaFim * 60 && (
                    <div
                      className={classes.agoraRotulo}
                      style={{ top: ((minutoAgora - horaInicio * 60) / 60) * ALTURA_HORA }}
                    >
                      {format(agora, "HH:mm")}
                    </div>
                  )}
              </div>

              {dias.map((dia) => {
                const passado = startOfDay(dia) < hoje;
                const ehHoje = isSameDay(dia, hoje);
                const fimDeSemana = dia.getDay() === 0 || dia.getDay() === 6;
                const eventos = distribuirColunas(eventosDoDia(dia));
                const alturaPassada = passado
                  ? altura
                  : ehHoje
                  ? Math.min(altura, Math.max(0, ((minutoAgora - horaInicio * 60) / 60) * ALTURA_HORA))
                  : 0;
                return (
                  <div
                    key={dia.toISOString()}
                    className={[
                      classes.colunaDia,
                      fimDeSemana ? classes.colunaFimDeSemana : "",
                      ehHoje ? classes.colunaHoje : "",
                      podeEditar ? classes.podeCriar : "",
                    ].join(" ")}
                    style={{ height: altura }}
                    onMouseDown={(e) => aoPressionar(e, dia)}
                  >
                    {alturaPassada > 0 && <div className={classes.passado} style={{ height: alturaPassada }} />}

                    {ehHoje && minutoAgora >= horaInicio * 60 && minutoAgora <= horaFim * 60 && (
                      <div
                        className={classes.agora}
                        style={{ top: ((minutoAgora - horaInicio * 60) / 60) * ALTURA_HORA }}
                      />
                    )}

                    {eventos.map((ev) => {
                      const cor = P[ev.status] || P.available;
                      const topo = ((ev.inicio - horaInicio * 60) / 60) * ALTURA_HORA + 1;
                      const alt = Math.max(((ev.fim - ev.inicio) / 60) * ALTURA_HORA - 3, 20);
                      const largura = 100 / ev.totalColunas;
                      const ini = new Date(ev.startAt);
                      const fim = new Date(ev.endAt);
                      const jaPassou = fim.getTime() < Date.now();
                      const marcado = ev.status === "booked";
                      const duasLinhas = alt >= 44;
                      return (
                        <Tooltip
                          key={ev.id}
                          arrow
                          enterDelay={400}
                          title={`${format(ini, "HH:mm")} – ${format(fim, "HH:mm")} · ${rotuloStatus(ev.status)}${
                            ev.jobTitle ? ` · ${ev.jobTitle}` : ""
                          }${ev.location ? ` · ${ev.location}` : ""}`}
                        >
                          <div
                            className={`${classes.evento} ${jaPassou ? classes.eventoPassado : ""}`}
                            style={{
                              top: topo,
                              height: alt,
                              left: `calc(${ev.coluna * largura}% + 4px)`,
                              width: `calc(${largura}% - 8px)`,
                              background: cor.fundo,
                              color: cor.texto,
                              "--barra": cor.barra,
                              "--fundoHover": cor.fundoHover,
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => setDetalhe(ev)}
                          >
                            <div className={classes.eventoLinha}>
                              {marcado && <span className={classes.eventoAvatar}>{iniciais(ev.candidateName)}</span>}
                              {ev.status === "blocked" && <BlockRoundedIcon style={{ fontSize: 13 }} />}
                              <span className={classes.eventoHora}>
                                {format(ini, "HH:mm")}
                                {duasLinhas || visao === "dia" ? ` – ${format(fim, "HH:mm")}` : ""}
                              </span>
                              {!duasLinhas && <span className={classes.eventoTexto}>{textoDoEvento(ev)}</span>}
                            </div>
                            {duasLinhas && (
                              <div className={classes.eventoLinha}>
                                <span className={classes.eventoTexto}>
                                  {textoDoEvento(ev)}
                                  {marcado && ev.jobTitle ? ` · ${ev.jobTitle}` : ""}
                                </span>
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}

                    {selecao && isSameDay(selecao.dia, dia) && (
                      <div
                        className={classes.selecao}
                        style={{
                          top: ((selecao.inicio - horaInicio * 60) / 60) * ALTURA_HORA,
                          height: ((selecao.fim - selecao.inicio) / 60) * ALTURA_HORA,
                        }}
                      >
                        {hhmm(selecao.inicio)} – {hhmm(selecao.fim)}
                      </div>
                    )}
                  </div>
                );
              })}

              {semNada && (
                <div className={classes.dica} onMouseDown={(e) => e.stopPropagation()}>
                  <EventAvailableRoundedIcon className={classes.dicaIcone} />
                  <div className={classes.dicaTitulo}>
                    {visao === "dia" ? "Nenhum horário neste dia" : "Nenhum horário nesta semana"}
                  </div>
                  <div className={classes.dicaTexto}>
                    {podeEditar
                      ? "Cadastre os horários livres para a assistente oferecer aos candidatos. Você também pode arrastar direto na grade."
                      : "O RH ainda não cadastrou horários para este período."}
                  </div>
                  {podeEditar && (
                    <Button className={classes.botaoPrimario} startIcon={<AddIcon />} onClick={() => abrirModal("cadastrar")}>
                      Cadastrar horários
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Mes ──────────────────────────────────────────────────────────────
  const renderMes = () => {
    const nomes = Array.from({ length: 7 }, (_, i) =>
      format(addDays(startOfWeek(new Date(), { weekStartsOn: 0 }), i), celular ? "EEEEE" : "EEE", {
        locale: ptBR,
      }).replace(".", "")
    );
    const limite = celular ? 1 : 3;
    return (
      <div className={classes.mes}>
        {nomes.map((n, i) => (
          <div key={i} className={classes.mesCabecalho}>
            {n}
          </div>
        ))}
        {dias.map((dia) => {
          const doDia = eventosDoDia(dia);
          const livres = doDia.filter((e) => e.status === "available").length;
          const marcadas = doDia.filter((e) => e.status === "booked").sort((a, b) => a.inicio - b.inicio);
          const ehHoje = isSameDay(dia, new Date());
          return (
            <div
              key={dia.toISOString()}
              className={`${classes.mesCelula} ${isSameMonth(dia, referencia) ? "" : classes.mesForaDoMes}`}
              onClick={() => irParaDia(dia)}
            >
              <div className={`${classes.mesNumero} ${ehHoje ? classes.numeroHoje : ""}`}>{format(dia, "d")}</div>
              {marcadas.slice(0, limite).map((ev) => (
                <div
                  key={ev.id}
                  className={classes.mesItem}
                  style={{ background: P.booked.fundo, color: P.booked.texto, borderLeft: `3px solid ${P.booked.barra}` }}
                >
                  {format(new Date(ev.startAt), "HH:mm")} {celular ? "" : ev.candidateName || ""}
                </div>
              ))}
              {marcadas.length > limite && (
                <div className={classes.mesItem} style={{ color: P.booked.suave }}>
                  +{marcadas.length - limite} entrevista(s)
                </div>
              )}
              {livres > 0 && (
                <div
                  className={classes.mesItem}
                  style={{ background: P.available.fundo, color: P.available.texto, borderLeft: `3px solid ${P.available.barra}` }}
                >
                  {livres} {livres === 1 ? "horário livre" : "horários livres"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Mini calendario ─────────────────────────────────────────────────
  const marcasMini = useMemo(() => {
    const mapa = {};
    doMesMini.forEach((h) => {
      const k = format(new Date(h.startAt), "yyyy-MM-dd");
      if (!mapa[k]) mapa[k] = { livre: false, marcada: false };
      if (h.status === "available") mapa[k].livre = true;
      if (h.status === "booked") mapa[k].marcada = true;
    });
    return mapa;
  }, [doMesMini]);

  const diaNaVisao = (d) =>
    visao === "mes" ? false : visao === "dia" ? isSameDay(d, referencia) : dias.some((x) => isSameDay(x, d));

  const nomeMesMini = maiuscula(format(mesMini, "MMMM 'de' yyyy", { locale: ptBR }));

  return (
    <Paper className={classes.root} elevation={0} square>
      <div className={classes.topo}>
        <div>
          <div className={classes.titulo}>
            <span className={classes.tituloIcone}>
              <EventRoundedIcon />
            </span>
            <div>
              Agenda de Entrevistas
              <div className={classes.subtitulo}>
                {podeEditar
                  ? "Cadastre os horários livres. A assistente oferece aos candidatos e agenda sozinha."
                  : "Horários livres e entrevistas marcadas pela equipe e pela assistente."}
              </div>
            </div>
          </div>
        </div>
        {podeEditar && (
          <div className={classes.acoes}>
            <Button className={classes.botaoSecundario} startIcon={<DeleteOutlineIcon />} onClick={() => abrirModal("remover")}>
              Remover horários
            </Button>
            <Button className={classes.botaoPrimario} startIcon={<AddIcon />} onClick={() => abrirModal("cadastrar")}>
              Cadastrar horários
            </Button>
          </div>
        )}
      </div>

      <div className={classes.corpo}>
        {/* ── Lateral ── */}
        <div className={classes.lateral}>
          <div className={classes.cartao}>
            <div className={classes.miniTopo}>
              <span className={classes.miniMes}>{nomeMesMini}</span>
              <span>
                <IconButton size="small" onClick={() => setMesMini((m) => addMonths(m, -1))} aria-label="Mês anterior">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setMesMini((m) => addMonths(m, 1))} aria-label="Próximo mês">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </div>
            <div className={classes.miniGrade}>
              {["D", "S", "T", "Q", "Q", "S", "S"].map((n, i) => (
                <div key={i} className={classes.miniNome}>
                  {n}
                </div>
              ))}
              {diasMini.map((d) => {
                const k = format(d, "yyyy-MM-dd");
                const marca = marcasMini[k];
                return (
                  <button
                    key={k}
                    type="button"
                    className={[
                      classes.miniDia,
                      isSameMonth(d, mesMini) ? "" : classes.miniFora,
                      isSameDay(d, new Date()) ? classes.miniHoje : "",
                      diaNaVisao(d)
                        ? visao === "dia" || isSameDay(d, new Date())
                          ? classes.miniSelecionado
                          : classes.miniNaSemana
                        : "",
                    ].join(" ")}
                    onClick={() => {
                      setReferencia(startOfDay(d));
                      if (visao === "mes") setVisao("dia");
                    }}
                  >
                    {format(d, "d")}
                    {marca && !(diaNaVisao(d) && (visao === "dia" || isSameDay(d, new Date()))) && (
                      <span className={classes.miniPontos}>
                        {marca.livre && <span className={classes.miniPonto} style={{ background: "#14b8a6" }} />}
                        {marca.marcada && <span className={classes.miniPonto} style={{ background: "#3b82f6" }} />}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={classes.cartao}>
            <div className={classes.cartaoTitulo}>Neste período</div>
            {[
              { s: "available", r: "Horários livres", v: resumo.livres },
              { s: "booked", r: "Entrevistas marcadas", v: resumo.marcadas },
              { s: "blocked", r: "Bloqueados", v: resumo.bloqueados },
            ].map((x) => (
              <div key={x.s} className={classes.legendaLinha}>
                <span
                  className={classes.legendaCor}
                  style={{ background: P[x.s].fundo, boxShadow: `inset 3px 0 0 ${P[x.s].barra}, 0 0 0 1px ${P[x.s].barra}55` }}
                />
                {x.r}
                <b>{x.v}</b>
              </div>
            ))}
          </div>

          <div className={classes.cartao}>
            <div className={classes.cartaoTitulo}>
              <PersonRoundedIcon style={{ fontSize: 16, color: "#2563eb" }} /> Próximas entrevistas
            </div>
            {proximasVisiveis.length === 0 && (
              <div className={classes.vazio}>
                Nenhuma entrevista marcada ainda. Quando a assistente agendar, ela aparece aqui.
              </div>
            )}
            {proximasVisiveis.slice(0, 8).map((p) => {
              const d = new Date(p.startAt);
              return (
                <div
                  key={p.id}
                  className={classes.proxima}
                  onClick={() => {
                    setDetalhe(p);
                    setReferencia(startOfDay(d));
                  }}
                >
                  <div className={classes.avatar}>{iniciais(p.candidateName)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className={classes.proximaNome}>{p.candidateName || "Candidato"}</div>
                    <div className={classes.proximaSub}>
                      {maiuscula(format(d, "EEE, d MMM · HH:mm", { locale: ptBR }))}
                      {p.jobTitle ? ` · ${p.jobTitle}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Calendario ── */}
        <div className={classes.calendario}>
          <div className={classes.barra}>
            <Button className={classes.botaoHoje} size="small" onClick={() => setReferencia(startOfDay(new Date()))}>
              Hoje
            </Button>
            <IconButton size="small" onClick={() => navegar(-1)} aria-label="Anterior">
              <ChevronLeftIcon />
            </IconButton>
            <IconButton size="small" onClick={() => navegar(1)} aria-label="Próximo">
              <ChevronRightIcon />
            </IconButton>
            <div className={classes.tituloPeriodo}>{maiuscula(textoPeriodo())}</div>
            <div className={classes.grupoVisao}>
              {[
                ["dia", "Dia"],
                ["semana", "Semana"],
                ["mes", "Mês"],
              ].map(([v, r]) => (
                <button
                  key={v}
                  type="button"
                  className={`${classes.botaoVisao} ${visao === v ? classes.botaoVisaoAtivo : ""}`}
                  onClick={() => setVisao(v)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          {visao === "mes" ? renderMes() : renderGrade()}
        </div>
      </div>

      <NovoHorarioModal
        open={novoAberto}
        inicial={novoInicial}
        modoInicial={novoModo}
        usuarios={usuarios}
        onClose={() => setNovoAberto(false)}
        onRemovido={recarregarTudo}
        onSalvo={() => {
          setNovoAberto(false);
          rolouRef.current = "";
          recarregarTudo();
        }}
      />

      <DetalheHorarioModal
        horario={detalhe}
        usuarios={usuarios}
        podeEditar={podeEditar}
        onClose={() => setDetalhe(null)}
        onAlterado={recarregarTudo}
      />
    </Paper>
  );
};

export default Agenda;
